import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { ComentarioUICamViewModel, TwUnidadInspeccionViewModelExtended } from '../../unidad-inspeccion-datosbasicos.types';
import { catchError, of, tap } from 'rxjs';
import { ComentarioUiService } from '../../comentario-ui.service';
import { UnidadInspeccionService } from '../../unidad-inspeccion.service';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';
import { EnumCodigosTwTcAcciones } from 'src/app/components/_importacion/solicitudes/types/enum';

@Component({
    selector: 'app-comentario-ui-wrapper-edit',
    templateUrl: './comentario-ui-wrapper-edit.component.html',
    styleUrls: ['./comentario-ui-wrapper-edit.component.scss'],
})
export class ComentarioUiWrapperEditComponent extends ComponentBase implements OnInit {
    private IdUi: number;
    visibilitySpinnerGuardando = false;
    private comentarioUI: ComentarioUICamViewModel;
    private comentariosUIListUrl = '/campanias/unidadinspeccion/comentarios/';
    private savingSuccessMessage = 'Comentario de U.I. guardado correctamente';

    private unidadInspeccion: TwUnidadInspeccionViewModelExtended;
    formGroup: FormGroup;

    private activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const idUi: any = params.get('idUi');
        this.IdUi = idUi;
        this.getUnidadInspeccion(idUi);
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private unidadInspeccionService: UnidadInspeccionService,
        private comentarioUICamService: ComentarioUiService,
        private navMenuService: NavMenuService,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
    }

    private preparePresentation(): void {
        this.buildForm();
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            Observaciones: ['', Validators.required],
        });
        this.form = this.formGroup;
    }

    /**
     * Se cargan los datos de la unidad de inspección y sus recintos "PI-A-Ed" asociados
     * Una vez obtenida, establece el valor para la propiedad readonly a partir de la información del usuario y los permisos de edición.
     * Después invoca al servicio de obtención de inspecciones de la U.I.
     * @param id number
     */
    private getUnidadInspeccion(id: number): void {
        this.unidadInspeccionService
            .getUI(id)
            .pipe(
                tap((udInspeccion: TwUnidadInspeccionViewModelExtended) => {
                    this.unidadInspeccion = udInspeccion;
                    this.navMenuService.updateMenuServiceForUdInspeccion(
                        udInspeccion.IdUi!,
                        udInspeccion.IdEstadoUi!,
                        udInspeccion.FirstRecinto.IdTipoSolicitudCam || 0,
                        udInspeccion.PeriodoCertificaKgs,
                    );
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Invocación de la API para salvar los cambios realizados en el PV
     * @return void
     */
    onClickSave(): void {
        if (!this.form.valid) {
            this.addErrorMessage('Indique unas observaciones');
            return;
        }

        this.comentarioUI = new ComentarioUICamViewModel();
        this.comentarioUI.Observaciones = this.form.controls['Observaciones'].value;
        this.comentarioUI.IdUnidadInspeccionCam = this.IdUi;

        this.submitted = true;
        this.visibilitySpinnerGuardando = true;

        this.comentarioUICamService
            .saveComentario(this.comentarioUI)
            .pipe(
                tap(() => {
                    this.addSuccessMessage(this.savingSuccessMessage);
                    this.visibilitySpinnerGuardando = false;
                    this.onBack();
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinnerGuardando = false;
                    return of(null);
                }),
            )
            .subscribe();
    }

    onBack(): void {
        this.router.navigate([this.comentariosUIListUrl + this.IdUi]);
    }

    get hasSavingAccess(): boolean {
        if (!this.IdUi || !this.unidadInspeccion) return false;

        const hasAddingAccess = this.loggedInUser!.getAction(
            EnumCodigosTwTcAcciones.ADD_COMENTARIO_UI,
            this.unidadInspeccion.IdEstadoUi,
            this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam,
        ).Editable;

        return hasAddingAccess && this.unidadInspeccion?.FirstRecinto?.PeriodoEnPlazoVigente;
    }

    get actionNotAllowed(): boolean {
        return this.visibilitySpinnerGuardando || this.formInvalid || !this.unidadInspeccion?.FirstRecinto?.PeriodoEnPlazoVigente;
    }

    private get formInvalid(): boolean {
        return !this.formGroup.valid || !this.formGroup.dirty || this.formGroup.pristine;
    }
}
